import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';
import styles from './ReceiptsTable.scss';
import { fromJS } from 'immutable';
import { AutoSizer, Column, Table, SortDirection } from 'react-virtualized';

class ReceiptsTable extends React.Component {
    static propTypes = {
        actions: PropTypes.object,
        receipts: PropTypes.object,
        selectedReceipts: PropTypes.object,
        selectionEnabled: PropTypes.bool,
        isFetching: PropTypes.bool,
        onRowSelection: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.state = {
            disableHeader: false,
            headerHeight: 30,
            height: 270,
            hideIndexRow: false,
            overscanRowCount: 100,
            rowHeight: 40,
            rowCount: 1000,
            scrollToIndex: undefined,
            sortBy: 'date',
            sortDirection: SortDirection.DESC,
            useDynamicRowHeight: false,
        };
    }

    classForReceipt({ type, duplicate }) {
        const duplicate_class = duplicate ? 'duplicate' : 'no_duplicate';
        return 'receipt-type-box ' + type + ' ' + duplicate_class;
    }

    abbrForReceiptType(receiptType) {
        switch (receiptType) {
            case 'order':
                return 'BEST';
            case 'stocktaking':
                return 'INV';
            case 'rearrangement':
                return 'UML';
            case 'stock_flow_adjustment':
                return 'BES';
            case 'goods_receipt':
                return 'EIN';
            case 'price_change':
                return 'PRE';
            case 'customer_order':
                return 'BEST';
            case 'retour':
                return 'RET';
            default:
                return 'BEST';
        }
    }

    formatDate(date) {
        if (!date) {
            return '';
        }
        return moment(date).format('DD.MM.YYYY');
    }

    formatDateWithTime(date) {
        if (!date) {
            return '';
        }
        return moment(date).format('DD.MM.YYYY');
    }

    formatTime(date) {
        return moment(date).format('HH:mm');
    }

    _noRowsRenderer = () => {
        return <div />;
    };

    _getDatum = (list, index) => {
        const receipt = list.get(index);
        return {
            enabled:
                this.props.selectedBranch !== null
                    ? receipt.get('branch') === this.props.selectedBranch
                    : true,
            selected: this.props.selectedReceipts.includes(receipt.get('id')),
            id: receipt.get('id'),
            receipt_id: receipt.get('receipt_id'),
            amount: receipt.get('amount'),
            user: receipt.get('user'),
            warehouse: receipt.get('warehouse'),
            type: receipt.get('type'),
            branch: receipt.get('branch'),
            duplicate: receipt.get('duplicate') | false,
            printed_at: this.formatDateWithTime(receipt.get('printed_at')),
            exported_at: this.formatDateWithTime(receipt.get('exported_at')),
            date: this.formatDateWithTime(receipt.get('date')),
            time: this.formatTime(receipt.get('date')),
        };
    };

    handleEventOnRowClick = data => {
        const { sortBy, sortDirection } = this.state;

        const sorted = (this.props.receipts || fromJS([]))
            .sortBy(item => {
                if (sortBy === 'exported_at' || sortBy === 'printed_at' || sortBy === 'date') {
                    const result = moment(item.get(sortBy)).unix();
                    return result || 0;
                } else {
                    return parseInt(item.get(sortBy));
                }
            })
            .update(list => (sortDirection === SortDirection.DESC ? list.reverse() : list));

        const rowGetter = ({ index }) => {
            return this._getDatum(sorted || fromJS([]), index);
        };

        const row = rowGetter(data);

        this.props.onRowClick(row);
    };

    _sort = ({ sortBy, sortDirection }) => {
        this.setState({ sortBy, sortDirection });
    };

    render = () => {
        const { overscanRowCount, sortBy, sortDirection } = this.state;

        const sorted = (this.props.receipts || fromJS([]))
            .sortBy(item => {
                if (sortBy === 'exported_at' || sortBy === 'printed_at' || sortBy === 'date') {
                    const result = moment(item.get(sortBy)).unix();
                    return result || 0;
                } else {
                    return parseInt(item.get(sortBy));
                }
            })
            .update(list => (sortDirection === SortDirection.DESC ? list.reverse() : list));

        const rowGetter = ({ index }) => this._getDatum(sorted, index);

        const rowCount = this.props.receipts.size;

        return (
            <AutoSizer disableHeight>
                {({ width }) => (
                    <Table
                        ref="Table"
                        height={500}
                        headerClassName={styles.tableHeader}
                        rowClassName={styles.tableRow}
                        headerHeight={55}
                        rowHeight={50}
                        noRowsRenderer={this._noRowsRenderer}
                        rowGetter={rowGetter}
                        rowCount={rowCount}
                        sort={this._sort}
                        sortBy={sortBy}
                        sortDirection={sortDirection}
                        overscanRowCount={overscanRowCount}
                        onRowClick={this.handleEventOnRowClick}
                        width={width}>
                        <Column
                            dataKey="selected"
                            label=""
                            width={width * 0.05}
                            cellRenderer={({ cellData, columnData, dataKey, rowData }) => (
                                <input
                                    type="checkbox"
                                    style={{ pointerEvents: rowData['enabled'] ? 'all' : 'none' }}
                                    checked={rowData['selected'] ? 'checked' : ''}
                                    onClick={click => {
                                        click.stopPropagation();
                                        this.props.onRowSelection(rowData['id']);
                                    }}
                                />
                            )}
                        />
                        <Column
                            dataKey="type"
                            label="Typ"
                            width={width * 0.07}
                            cellRenderer={({ cellData, columnData, dataKey, rowData }) => (
                                <div className={this.classForReceipt(rowData)}>
                                    {this.abbrForReceiptType(cellData)}
                                </div>
                            )}
                        />
                        <Column
                            dataKey="date"
                            label="Datum"
                            width={width * 0.14}
                            cellRenderer={({ cellData, columnData, dataKey, rowData }) => (
                                <span>
                                    {rowData['date']}
                                    <br />
                                    {rowData['time']}
                                </span>
                            )}
                        />
                        <Column dataKey="receipt_id" label="Beleg" width={width * 0.14} />
                        <Column dataKey="user" label="Erfasser" width={width * 0.14} />
                        <Column width={width * 0.14} label="Menge" dataKey="amount" />
                        <Column dataKey="branch" label="Filiale" width={width * 0.14} />
                        <Column dataKey="warehouse" label="Regal" width={width * 0.14} />
                        <Column
                            dataKey="exported_at"
                            label="Exportiert am"
                            width={width * 0.14}
                            cellRenderer={({ cellData }) => <span>{cellData}</span>}
                        />
                        <Column
                            dataKey="printed_at"
                            label="Gedruckt am"
                            width={width * 0.14}
                            cellRenderer={({ cellData }) => <span> {cellData}</span>}
                        />
                    </Table>
                )}
            </AutoSizer>
        );
    };
}
export default ReceiptsTable;
